import React, {useEffect, useMemo} from "react";
import {Col, Form, Label, Modal, ModalBody, ModalHeader, Row} from "reactstrap";
import {useDispatch, useSelector} from "react-redux";
import * as yup from "yup";
import {useFormik} from "formik";
import LabelInput from "../../../components/Common/LabelInput";
// import {addTeam, clearTeam, modalTeam, updTeam} from "../../../store/team/actions";
import {addBroker, clearBroker, modalBroker, updBroker} from "../../../store/brokers/actions";
import Select from "../../../components/Common/Select";
import {clearCrmSystems, getCrmSystems} from "../../../store/crmSystems/actions";
import {ICrmSystem} from "../../../store/crmSystems/types";
import usePermissions from "../../../hooks/usePermissions";
import useOffices from "../../../hooks/useOffices";


interface Props {
    isOpen: boolean;
    //toggle: () => void;
}

interface IFormValues {
    name: string;
    ci: string;
    crm_system_id: number;
    office_id: number;
}

const ModalBroker = ({isOpen}: Props) => {
    const dispatch = useDispatch();
    const {hasPermission} = usePermissions();

    const item = useSelector((state: any) => state.Brokers.item || {});
    const crmSystems = useSelector((state: any) => (state.CrmSystem.data || []) as ICrmSystem[]);
    const crmSystemsLoading = useSelector((state: any) => (state.CrmSystem.loading || false) as boolean);

    const {user_offices, user_office_ids} = useOffices()
    const defaultOffice = user_office_ids.length === 1 ? user_office_ids[0] : null;
    const officeSelect =
        user_offices.map((item: any) => ({
            value: item.id,
            label: item.name,
        }))

    const crmSystemsSelect = useMemo(() => (
        crmSystems.map((item: any) => ({
            value: item.id,
            label: item.name,
        }))
    ), [crmSystems]);

    useEffect(() => {
        dispatch(getCrmSystems('', {}))

        return () => {
            dispatch(clearCrmSystems())
        }
    }, []);

    const validationSchema: yup.Schema<IFormValues> = yup.object().shape({
        name: yup.string()
            .min(1, "min 1 character")
            .max(75, "max 75 characters")
            .matches(/^[a-zA-Z0-9 _-]+$/, "Only Latin letters, numbers, spaces, underscores, and hyphens")
            .required("required"),
        ci: yup.string()
            .min(1, "min 1 character")
            .max(5, "max 5 characters")
            .matches(/^[0-9]+$/, "Only numbers")
            .required("required"),
        crm_system_id: yup.number()
            .required("required"),
        office_id: yup.number()
            .min(0, "required")
            .required("required"),
    });

    const initialValues: IFormValues = {
        name: item?.name || "",
        ci: item?.ci || "",
        crm_system_id: item?.crm_system_id || "",
        office_id: item?.office_id || defaultOffice
    }

    const handleSubmitForm = () => {
        let ci = parseInt(values.ci, 10);
        const requestBroker = {name: values.name, crm_system_id: values.crm_system_id, office_id: values.office_id}

        if (item.id) {
            dispatch(updBroker({...requestBroker, "ci": values.ci}, item.id));
        } else {
            dispatch(addBroker({...requestBroker, "ci": ci}));
        }
    };

    const {
        values,
        validateForm,
        handleChange,
        submitForm,
        handleBlur,
        errors,
        touched,
        isValid,
        resetForm,
        setFieldValue
    } = useFormik({
        initialValues: initialValues,
        onSubmit: handleSubmitForm,
        validationSchema: validationSchema,
        validateOnBlur: true,
        validateOnChange: true,
        validateOnMount: true,
        enableReinitialize: true
    });

    const handlerClickSubmit = async () => {
        const curErrors = await validateForm();
        const curErrorsKeys = Object.keys(curErrors);
        if (curErrorsKeys.length) {
            const el = document.getElementById(curErrorsKeys[0]);
            if (el) el.focus();
        }
        submitForm();
    };

    const handleChangeSelect = (name: string, value: any) => {
        setFieldValue(name, value.value)
    }

    const close = () => {
        dispatch(modalBroker(false));
        dispatch(clearBroker());
        dispatch(clearCrmSystems())
        resetForm();
    };

    const shouldSelectOffice = hasPermission("create-brokers-full") || hasPermission("update-brokers-full")

    return (
        <Modal isOpen={isOpen} toggle={close}>
            <ModalHeader toggle={close} tag="h4">
                {item?.id ? "Edit" : "Add"} Broker
            </ModalHeader>
            <ModalBody>
                <Form className="needs-validation">
                    <Row>
                        <Col xs={12}>
                            <div className="mb-3">
                                <LabelInput
                                    label="Name*"
                                    placeholder="Name*"
                                    name="name"
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    value={values.name || ""}
                                    hasError={!!(errors.name && touched.name)}
                                    errorText={errors.name}
                                />
                            </div>
                            <div className="mb-3">
                                <LabelInput
                                    label="Ci*"
                                    placeholder="Ci*"
                                    name="ci"
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    value={values.ci || ""}
                                    hasError={!!(errors.ci && touched.ci)}
                                    errorText={errors.ci}
                                />
                            </div>
                            <div className='mb-3'>
                                <Label>CRM System: </Label>
                                <Select
                                    isSearchable
                                    name="crm_system_id"
                                    label="CRM System"
                                    placeholder="CRM System"
                                    isLoading={crmSystemsLoading}
                                    disabled={crmSystemsLoading}
                                    options={crmSystemsSelect}
                                    onChange={(value: any) => handleChangeSelect("crm_system_id", value)}
                                    value={crmSystemsSelect.find((item: any) => item.value === values.crm_system_id)}
                                />
                            </div>
                            {shouldSelectOffice && <div className='mb-3'>
                                <Label>Office: </Label>
                                <Select
                                    isSearchable
                                    name="office_id"
                                    label="Office"
                                    placeholder="Office"
                                    options={officeSelect}
                                    onChange={(value: any) => handleChangeSelect("office_id", value)}
                                    value={officeSelect.find((item: any) => item.value === values.office_id)}
                                />
                            </div>}
                        </Col>
                    </Row>
                    <Row>
                        <Col>
                            <div className="text-end">
                                <button
                                    type="button"
                                    className="btn btn-success save-user"
                                    onClick={handlerClickSubmit}
                                    disabled={!isValid}
                                > Save
                                </button>
                            </div>
                        </Col>
                    </Row>
                </Form>
            </ModalBody>
        </Modal>
    )
}

export default ModalBroker;